import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Card,
  Grid,
} from "@mui/material";
import { useHeader } from "../context/HeaderContext";
import {
  FaCheck,
  FaTrophy,
  FaStar,
  FaUsers,
  FaGift,
  FaClock,
  FaFileAlt,
  FaCoins,
  FaShieldAlt,
  FaLock,
} from "react-icons/fa";
import {
  useClaimNowMutation,
  useBuyReferralSpinMutation,
  useGetTotalPointsQuery,
  useGetBuyReferralSpinQuery,
} from "../services/leaderBoardApi";
import {
  useSpinWheelClaimDynamicMutation,
  useGetSpinWheelConfigQuery,
} from "../services/spinWheelApi";

const Spinwheel = ({ initialTab = "points" }) => {
  const { setScreenName } = useHeader();

  useEffect(() => {
    setScreenName("Spin & Win");
    return () => {
      setScreenName(null);
    };
  }, [setScreenName]);

  // Active Tab: 'points' | 'referrals'
  const [activeTab, setActiveTab] = useState(initialTab);

  // Spin Wheel Config from Backend
  const { data: adminConfigData } = useGetSpinWheelConfigQuery();

  // Dynamic Entry Points from Database (with [10, 20, 30] fallback, strictly no 100)
  const pointsEntryOptions = useMemo(() => {
    const entryPoints = adminConfigData?.entryPoints;
    if (Array.isArray(entryPoints) && entryPoints.length > 0) {
      return entryPoints.map(Number).sort((a, b) => a - b);
    }
    return [10, 20, 30];
  }, [adminConfigData?.entryPoints]);

  // Selected Entry:
  // Under 'points': 'free' or dynamic entry options
  // Under 'referrals': 1500, 3000, 5000
  const [selectedPointsEntry, setSelectedPointsEntry] = useState("free");
  const [selectedReferralEntry, setSelectedReferralEntry] = useState(1500);

  // If currently selected point was removed by admin, fallback gracefully
  useEffect(() => {
    if (selectedPointsEntry !== "free" && pointsEntryOptions.length > 0) {
      if (!pointsEntryOptions.includes(Number(selectedPointsEntry))) {
        setSelectedPointsEntry(pointsEntryOptions[0]);
      }
    }
  }, [pointsEntryOptions, selectedPointsEntry]);

  const { data: getBuyReferralData } = useGetBuyReferralSpinQuery();
  const dbCategories = getBuyReferralData?.data || [];

  // Wheel slice configurations matching mockups & backend win categories
  const freePointsWheel = useMemo(
    () => [
      { label: "0", sub: "Points" },
      { label: "2", sub: "Points" },
      { label: "4", sub: "Points" },
      { label: "6", sub: "Points" },
      { label: "8", sub: "Points" },
      { label: "10", sub: "Points" },
    ],
    []
  );

  // Dynamic Return Multipliers from Database
  const multiplierWheel = useMemo(() => {
    const multipliers = adminConfigData?.returnMultipliers;
    if (Array.isArray(multipliers) && multipliers.length > 0) {
      return multipliers.map((val) => ({
        label: `${val}x`,
        sub: "Multiplier",
      }));
    }
    return [
      { label: "0x", sub: "Multiplier" },
      { label: "0.25x", sub: "Multiplier" },
      { label: "0.5x", sub: "Multiplier" },
      { label: "1x", sub: "Multiplier" },
      { label: "1.25x", sub: "Multiplier" },
      { label: "1.5x", sub: "Multiplier" },
    ];
  }, [adminConfigData?.returnMultipliers]);

  const referralWheel1500 = useMemo(
    () => [
      { label: "0", sub: "Referrals" },
      { label: "1", sub: "Referral" },
      { label: "2", sub: "Referrals" },
      { label: "3", sub: "Referrals" },
      { label: "4", sub: "Referrals" },
      { label: "5", sub: "Referrals" },
    ],
    []
  );

  const referralWheel3000 = useMemo(
    () => [
      { label: "0", sub: "Referrals" },
      { label: "1", sub: "Referral" },
      { label: "2", sub: "Referrals" },
      { label: "4", sub: "Referrals" },
      { label: "6", sub: "Referrals" },
      { label: "10", sub: "Referrals" },
    ],
    []
  );

  const referralWheel5000 = useMemo(
    () => [
      { label: "2", sub: "Referrals" },
      { label: "5", sub: "Referrals" },
      { label: "8", sub: "Referrals" },
      { label: "10", sub: "Referrals" },
      { label: "15", sub: "Referrals" },
      { label: "20", sub: "Referrals" },
    ],
    []
  );

  const dynamicReferralWheel = useMemo(() => {
    const matchedDoc = dbCategories.find(
      (c) => Number(c.point) === Number(selectedReferralEntry)
    );
    if (matchedDoc && Array.isArray(matchedDoc.winCategory) && matchedDoc.winCategory.length > 0) {
      return matchedDoc.winCategory.map((val) => ({
        label: String(val),
        sub: val === 1 ? "Referral" : "Referrals",
      }));
    }
    if (selectedReferralEntry === 3000) return referralWheel3000;
    if (selectedReferralEntry === 5000) return referralWheel5000;
    return referralWheel1500;
  }, [dbCategories, selectedReferralEntry, referralWheel1500, referralWheel3000, referralWheel5000]);

  const activeWheelSlices = useMemo(() => {
    if (activeTab === "referrals") {
      return dynamicReferralWheel;
    }
    if (selectedPointsEntry === "free") return freePointsWheel;
    return multiplierWheel;
  }, [activeTab, selectedPointsEntry, dynamicReferralWheel, freePointsWheel, multiplierWheel]);

  const sliceColors = [
    "#651FFF",
    "#0084FF",
    "#00C853",
    "#FFAB00",
    "#FF3D00",
    "#E040FB",
  ];

  // API Hooks
  const [claimNow] = useClaimNowMutation();
  const [spinWheelClaim] = useSpinWheelClaimDynamicMutation();
  const [buyReferralSpin] = useBuyReferralSpinMutation();
  const { data: getTotalPoints, refetch: refetchTotalPoints } = useGetTotalPointsQuery();

  // Cooldown states
  const [lastSpinTime, setLastSpinTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [canSpinFree, setCanSpinFree] = useState(true);

  const [isSpinning, setIsSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [showWinModal, setShowWinModal] = useState(false);
  const [wonPoints, setWonPoints] = useState(0);

  useEffect(() => {
    const stored = localStorage.getItem("lastSpinTime");
    if (stored) {
      setLastSpinTime(stored);
    }
  }, []);

  useEffect(() => {
    const checkTimer = () => {
      if (!lastSpinTime) {
        setCanSpinFree(true);
        setTimeRemaining(0);
        return;
      }
      const now = new Date();
      const lastSpin = new Date(lastSpinTime);

      // Calculate top of the next clock hour following lastSpin
      const nextHour = new Date(lastSpin);
      nextHour.setMinutes(0, 0, 0);
      nextHour.setHours(nextHour.getHours() + 1);

      if (now >= nextHour) {
        setCanSpinFree(true);
        setTimeRemaining(0);
      } else {
        setCanSpinFree(false);
        const remaining = Math.ceil((nextHour.getTime() - now.getTime()) / 1000);
        setTimeRemaining(remaining > 0 ? remaining : 0);
      }
    };
    checkTimer();
    const interval = setInterval(checkTimer, 1000);
    return () => clearInterval(interval);
  }, [lastSpinTime]);

  const formatTimer = () => {
    if (!timeRemaining || timeRemaining <= 0) return "00 : 00 : 00";
    const h = Math.floor(timeRemaining / 3600).toString().padStart(2, "0");
    const m = Math.floor((timeRemaining % 3600) / 60).toString().padStart(2, "0");
    const s = (timeRemaining % 60).toString().padStart(2, "0");
    return `${h} : ${m} : ${s}`;
  };

  const getTargetRotationForIndex = (index) => {
    const segmentAngle = 360 / activeWheelSlices.length;
    const targetAngle = index * segmentAngle + segmentAngle / 2;
    const finalAngle = 360 - targetAngle;
    return 360 * 8 + finalAngle;
  };

  const handleSpinNow = async () => {
    if (isSpinning) return;
    const userPoints = getTotalPoints?.currentPoint || 0;

    if (activeTab === "points" && selectedPointsEntry !== "free") {
      if (userPoints < selectedPointsEntry) {
        alert("Not enough points for this entry.");
        return;
      }
    } else if (activeTab === "referrals") {
      if (userPoints < selectedReferralEntry) {
        alert("Not enough points for this referral spin.");
        return;
      }
    } else if (activeTab === "points" && selectedPointsEntry === "free") {
      if (!canSpinFree) {
        alert("Free spin is on 1-hour cooldown!");
        return;
      }
    }

    setIsSpinning(true);
    setShowWinModal(false);

    try {
      let winningIndex = 0;
      let wonVal = 0;

      if (activeTab === "points") {
        if (selectedPointsEntry === "free") {
          const res = await claimNow({ status: true });
          const point = res?.data?.point ?? 0;
          const idx = freePointsWheel.findIndex((s) => Number(s.label) === Number(point));
          winningIndex = idx !== -1 ? idx : Math.floor(Math.random() * freePointsWheel.length);
          wonVal = point;

          const nowIso = new Date().toISOString();
          localStorage.setItem("lastSpinTime", nowIso);
          setLastSpinTime(nowIso);
        } else {
          const res = await spinWheelClaim({ amount: selectedPointsEntry });
          if (res?.data?.point !== undefined || res?.data?.wonPoints !== undefined) {
            const pointVal = res?.data?.point ?? "0x";
            const wonPointsNum = res?.data?.wonPoints !== undefined
              ? Number(res.data.wonPoints)
              : (Number(String(pointVal).replace("x", "")) * Number(selectedPointsEntry));
            wonVal = isNaN(wonPointsNum) ? 0 : wonPointsNum;

            const cleanPointStr = String(pointVal).toLowerCase().trim();
            const idx = multiplierWheel.findIndex(
              (s) => s.label.toLowerCase().trim() === cleanPointStr || s.label.replace("x", "").trim() === cleanPointStr.replace("x", "")
            );
            winningIndex = idx !== -1 ? idx : 0;
          } else {
            const msg = res?.error?.data?.message || res?.data?.message || "Spin failed";
            throw new Error(msg);
          }
        }
      } else {
        // Referrals tab spin
        const res = await buyReferralSpin({ amount: selectedReferralEntry });
        const resData = res?.data;

        if (resData?.success || resData?.wonReferral !== undefined || resData?.wonReferrals !== undefined) {
          const refVal = resData?.wonReferral ?? resData?.wonReferrals ?? 0;
          wonVal = refVal;
          const idx = activeWheelSlices.findIndex((s) => Number(s.label) === Number(refVal));
          if (idx !== -1) {
            winningIndex = idx;
          } else {
            let minDiff = Infinity;
            let closestIdx = 0;
            activeWheelSlices.forEach((s, i) => {
              const diff = Math.abs(Number(s.label) - Number(refVal));
              if (diff < minDiff) {
                minDiff = diff;
                closestIdx = i;
              }
            });
            winningIndex = closestIdx;
          }
        } else {
          const msg = res?.error?.data?.message || resData?.message || "Referral spin failed";
          throw new Error(msg);
        }
      }

      const targetRotation = getTargetRotationForIndex(winningIndex);
      const newRotation = Math.floor(wheelRotation / 360) * 360 + targetRotation + 360 * 5;
      setWheelRotation(newRotation);

      setTimeout(() => {
        setIsSpinning(false);
        setWonPoints(wonVal);
        setShowWinModal(true);
        if (refetchTotalPoints) refetchTotalPoints();
      }, 4500);
    } catch (err) {
      console.error(err);
      setIsSpinning(false);
      let errMsg = err?.data?.message || err?.message || "An error occurred during spin.";
      if (errMsg === "Service denied") {
        errMsg = "Referral service is disabled for your account. Please contact support.";
      }
      alert(errMsg);
    }
  };

  const segmentAngle = 360 / activeWheelSlices.length;
  const radius = 130;
  const centerX = 140;
  const centerY = 140;

  return (
    <Box sx={{ maxWidth: 640, margin: "0 auto", p: 2, fontFamily: "'Inter', sans-serif" }}>
      {/* 1. TOP TAB SWITCHER */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
        <Card
          onClick={() => setActiveTab("points")}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            p: 1.5,
            cursor: "pointer",
            borderRadius: "14px",
            border: activeTab === "points" ? "2px solid #651FFF" : "1px solid #E2E8F0",
            boxShadow: activeTab === "points" ? "0 4px 12px rgba(101, 31, 255, 0.15)" : "none",
            position: "relative",
          }}
        >
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#651FFF", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", mr: 1.5 }}>
            <FaStar size={14} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13, color: "#1E293B" }}>
              Spin & Win Points
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B", fontSize: 10 }}>
              Win points instantly
            </Typography>
          </Box>
          {activeTab === "points" && (
            <Box sx={{ position: "absolute", top: -6, right: -4, width: 20, height: 20, borderRadius: "50%", bgcolor: "#651FFF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
              <FaCheck size={10} />
            </Box>
          )}
        </Card>

        <Card
          onClick={() => setActiveTab("referrals")}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            p: 1.5,
            cursor: "pointer",
            borderRadius: "14px",
            border: activeTab === "referrals" ? "2px solid #651FFF" : "1px solid #E2E8F0",
            boxShadow: activeTab === "referrals" ? "0 4px 12px rgba(101, 31, 255, 0.15)" : "none",
            position: "relative",
          }}
        >
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", color: "#555", mr: 1.5 }}>
            <FaUsers size={14} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13, color: "#1E293B" }}>
              Spin & Win Referrals
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B", fontSize: 10 }}>
              Win referral multipliers
            </Typography>
          </Box>
          {activeTab === "referrals" && (
            <Box sx={{ position: "absolute", top: -6, right: -4, width: 20, height: 20, borderRadius: "50%", bgcolor: "#651FFF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
              <FaCheck size={10} />
            </Box>
          )}
        </Card>
      </Box>

      {/* 2. PLAYING CONTEXT BANNER */}
      <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#F3E8FF", borderRadius: "12px", p: 1.5, mb: 3, border: "1px solid #E9D5FF" }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: "#651FFF", mr: 1.5 }}>
          <FaTrophy size={18} />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontSize: 12, color: "#1E293B" }}>
            <b>You are playing: </b>
            <span style={{ color: "#651FFF", fontWeight: 700 }}>
              {activeTab === "points" ? "Spin & Win Points" : "Spin & Win Referrals"}
            </span>
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10, color: "#64748B" }}>
            {activeTab === "points"
              ? "Spin the wheel by using your points and win exciting rewards."
              : "Spin the wheel by using your points and win referral multipliers."}
          </Typography>
        </Box>
      </Box>

      {/* 3. WHEEL SVG & POINTER */}
      <Box sx={{ position: "relative", width: 280, height: 280, mx: "auto", my: 2 }}>
        {/* Top Pointer */}
        <Box
          sx={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "14px solid transparent",
            borderRight: "14px solid transparent",
            borderTop: "24px solid #651FFF",
            zIndex: 10,
          }}
        />

        {/* Wheel SVG */}
        <Box
          sx={{
            width: 280,
            height: 280,
            transformOrigin: "center center",
            transform: `rotate(${wheelRotation}deg)`,
            transition: isSpinning ? "transform 4.5s cubic-bezier(0.15, 0.9, 0.25, 1)" : "none",
          }}
        >
          <svg width="280" height="280" viewBox="0 0 280 280">
            {activeWheelSlices.map((slice, index) => {
              const startAngle = index * segmentAngle - 90;
              const endAngle = (index + 1) * segmentAngle - 90;
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;

              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);

              const largeArcFlag = segmentAngle > 180 ? 1 : 0;
              const pathData = [
                `M ${centerX} ${centerY}`,
                `L ${x1} ${y1}`,
                `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                `Z`,
              ].join(" ");

              const textAngle = startAngle + segmentAngle / 2;
              const textRad = (textAngle * Math.PI) / 180;
              const textX = centerX + radius * 0.62 * Math.cos(textRad);
              const textY = centerY + radius * 0.58 * Math.sin(textRad);
              const subY = textY + 16;

              return (
                <g key={index}>
                  <path d={pathData} fill={sliceColors[index]} stroke="#ffffff" strokeWidth="2" />
                  <text x={textX} y={textY} fill="#ffffff" fontSize="22" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                    {slice.label}
                  </text>
                  <text x={textX} y={subY} fill="#ffffff" fontSize="11" fontWeight="500" textAnchor="middle" dominantBaseline="middle">
                    {slice.sub}
                  </text>
                </g>
              );
            })}
            <circle cx="140" cy="140" r="38" fill="#ffffff" stroke="#651FFF" strokeWidth="3" />
          </svg>
        </Box>

        {/* Center Hub */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 76,
            height: 76,
            borderRadius: "50%",
            bgcolor: "#ffffff",
            border: "3px solid #651FFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#651FFF",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        >
          {activeTab === "points" ? <FaStar size={18} /> : <FaUsers size={16} />}
          <Typography variant="caption" sx={{ fontWeight: 800, fontSize: 11, mt: 0.2 }}>
            SPIN
          </Typography>
        </Box>
      </Box>

      {/* 4. CHOOSE YOUR ENTRY */}
      <Typography variant="subtitle2" align="center" sx={{ fontWeight: 800, fontSize: 11, color: "#651FFF", letterSpacing: 1, mt: 3, mb: 1.5 }}>
        ✦ CHOOSE YOUR ENTRY ✦
      </Typography>

      {activeTab === "points" ? (
        <Box sx={{ display: "flex", gap: 0.8, width: "100%" }}>
          <Card
            onClick={() => setSelectedPointsEntry("free")}
            sx={{
              flex: 1,
              p: 1,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: "10px",
              border: selectedPointsEntry === "free" ? "2px solid #651FFF" : "1px solid #E2E8F0",
              bgcolor: selectedPointsEntry === "free" ? "#FAF5FF" : "#ffffff",
              position: "relative",
            }}
          >
            {selectedPointsEntry === "free" && (
              <Box sx={{ position: "absolute", top: -4, right: -3, width: 14, height: 14, borderRadius: "50%", bgcolor: "#651FFF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FaCheck size={8} />
              </Box>
            )}
            <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: "#F3E8FF", display: "flex", alignItems: "center", justifyContent: "center", color: "#651FFF", mx: "auto", mb: 0.5 }}>
              <FaGift size={12} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 10, color: "#1E293B" }}>
              FREE
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B", fontSize: 8 }}>
              0 Pts
            </Typography>
          </Card>

          {pointsEntryOptions.map((amt) => (
            <Card
              key={amt}
              onClick={() => setSelectedPointsEntry(amt)}
              sx={{
                flex: 1,
                p: 1,
                textAlign: "center",
                cursor: "pointer",
                borderRadius: "10px",
                border: selectedPointsEntry === amt ? "2px solid #651FFF" : "1px solid #E2E8F0",
                bgcolor: selectedPointsEntry === amt ? "#FAF5FF" : "#ffffff",
                position: "relative",
              }}
            >
              {selectedPointsEntry === amt && (
                <Box sx={{ position: "absolute", top: -4, right: -3, width: 14, height: 14, borderRadius: "50%", bgcolor: "#651FFF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaCheck size={8} />
                </Box>
              )}
              <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFA500", mx: "auto", mb: 0.5 }}>
                <FaStar size={11} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 10, color: "#1E293B" }}>
                {amt}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B", fontSize: 8 }}>
                Pts
              </Typography>
            </Card>
          ))}
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {[1500, 3000, 5000].map((amt) => (
            <Grid item xs={4} key={amt}>
              <Card
                onClick={() => setSelectedReferralEntry(amt)}
                sx={{
                  p: 1.5,
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: "12px",
                  border: selectedReferralEntry === amt ? "2px solid #651FFF" : "1px solid #E2E8F0",
                  bgcolor: selectedReferralEntry === amt ? "#FAF5FF" : "#ffffff",
                  position: "relative",
                }}
              >
                {selectedReferralEntry === amt && (
                  <Box sx={{ position: "absolute", top: -5, right: -4, width: 16, height: 16, borderRadius: "50%", bgcolor: "#651FFF", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaCheck size={9} />
                  </Box>
                )}
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#F3E8FF", display: "flex", alignItems: "center", justifyContent: "center", color: "#651FFF", mx: "auto", mb: 0.8 }}>
                  <FaUsers size={14} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 12, color: "#1E293B" }}>
                  {amt}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748B", fontSize: 9 }}>
                  Points
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 5. COUNTDOWN TIMER BAR */}
      {activeTab === "points" && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#ffffff", borderRadius: "10px", p: 1.2, mt: 2, border: "1px solid #E2E8F0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <FaClock color="#651FFF" size={16} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#1E293B", fontSize: 11 }}>
              1 Free Spin every 1 hour
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontSize: 9, mr: 0.5 }}>
              Next free spin available in
            </Typography>
            <Box sx={{ bgcolor: "#F3E8FF", px: 1, py: 0.3, borderRadius: "6px", color: "#651FFF", fontWeight: 800, fontSize: 10 }}>
              {formatTimer()}
            </Box>
          </Box>
        </Box>
      )}

      {/* 6. PRIMARY ACTION BUTTON */}
      <Button
        disabled={isSpinning}
        onClick={handleSpinNow}
        fullWidth
        sx={{
          mt: 2,
          py: 1.5,
          borderRadius: "12px",
          background: "linear-gradient(90deg, #651FFF 0%, #7C4DFF 100%)",
          color: "#ffffff",
          fontWeight: 800,
          fontSize: 14,
          textTransform: "none",
          boxShadow: "0 4px 14px rgba(101, 31, 255, 0.3)",
          "&:hover": {
            background: "linear-gradient(90deg, #5311E2 0%, #6A3BE2 100%)",
          },
        }}
      >
        {activeTab === "points"
          ? selectedPointsEntry === "free"
            ? "SPIN NOW >"
            : `SPIN NOW (${selectedPointsEntry} Points) >`
          : `SPIN NOW (${selectedReferralEntry} Points) >`}
      </Button>

      {/* 7. HOW IT WORKS FOOTER */}
      <Box sx={{ mt: 3, bgcolor: "#ffffff", borderRadius: "14px", p: 2, border: "1px solid #E2E8F0" }}>
        <Typography variant="subtitle2" align="center" sx={{ fontWeight: 800, fontSize: 11, color: "#651FFF", letterSpacing: 1, mb: 1.5 }}>
          ✦ HOW IT WORKS ✦
        </Typography>

        <Grid container spacing={1} alignItems="center" textAlign="center">
          <Grid item xs={3}>
            <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EDE7F6", color: "#651FFF", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 0.5 }}>
              <FaFileAlt size={14} />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 9, display: "block" }}>
              1. Choose Entry
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 8, color: "#64748B" }}>
              Select any entry option to spin.
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#E3F2FD", color: "#0084FF", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 0.5 }}>
              <FaStar size={14} />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 9, display: "block" }}>
              2. Spin Wheel
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 8, color: "#64748B" }}>
              Tap on the SPIN button.
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#E8F5E9", color: "#00C853", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 0.5 }}>
              <FaTrophy size={14} />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 9, display: "block" }}>
              3. See Result
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 8, color: "#64748B" }}>
              Wheel lands on a multiplier.
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#FFF8E1", color: "#FFAB00", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 0.5 }}>
              <FaCoins size={14} />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 9, display: "block" }}>
              {activeTab === "points" ? "4. Win Points" : "4. Get Referrals"}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 8, color: "#64748B" }}>
              Receive rewards instantly.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1, borderTop: "1px solid #F1F5F9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FaShieldAlt color="#651FFF" size={14} />
            <Typography variant="caption" sx={{ fontSize: 9, color: "#64748B" }}>
              Fair Play: 100% random and fair.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FaLock color="#651FFF" size={14} />
            <Typography variant="caption" sx={{ fontSize: 9, color: "#64748B" }}>
              Secure & Trusted data.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* WIN MODAL */}
      <Modal open={showWinModal} onClose={() => setShowWinModal(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 300, bgcolor: "#ffffff", borderRadius: "20px", p: 3, textAlign: "center", boxShadow: 24 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1, color: "#1E293B" }}>
            {Number(wonPoints) === 0 ? "SORRY!" : "CONGRATULATIONS!"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>
            {Number(wonPoints) === 0 ? (
              "Sorry better luck next time"
            ) : (
              <>
                You won <b style={{ color: "#651FFF" }}>{wonPoints}</b> {activeTab === "referrals" ? "Referral(s)" : "Points"}!
              </>
            )}
          </Typography>
          <Button fullWidth onClick={() => setShowWinModal(false)} sx={{ bgcolor: "#651FFF", color: "#fff", py: 1, borderRadius: "10px", fontWeight: 700, "&:hover": { bgcolor: "#5311E2" } }}>
            {Number(wonPoints) === 0 ? "OK" : "CLAIM REWARD"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default Spinwheel;
